import { z } from 'zod';

import {
  membershipRegisterSearchSchema,
  type MembershipRegisterSearch,
} from './membership-register-search';

const membershipDetailPathPattern =
  /^\/(en|ru)\/crm\/memberships\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/iu;
const customerMembershipPathPattern =
  /^\/(en|ru)\/crm\/(?:user-profiles|customers)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/membership$/iu;

function splitApplicationPath(
  value: string
): { pathname: string; search: string; hash: string } | undefined {
  if (!value.startsWith('/') || value.startsWith('//')) return undefined;

  const hashIndex = value.indexOf('#');
  const withoutHash = hashIndex === -1 ? value : value.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : value.slice(hashIndex);
  const queryIndex = withoutHash.indexOf('?');
  return queryIndex === -1
    ? { pathname: withoutHash, search: '', hash }
    : { pathname: withoutHash.slice(0, queryIndex), search: withoutHash.slice(queryIndex), hash };
}

/**
 * Keeps a Customer Profile opened from a Membership inside its authorized local
 * return path. The target is deliberately limited to a Membership detail page
 * so a query parameter can never become an external or arbitrary in-app redirect.
 */
export function normalizeCrmMembershipDetailReturnTo(
  value: string | undefined
): string | undefined {
  if (value === undefined) return undefined;
  const parsed = splitApplicationPath(value);
  if (parsed === undefined || !membershipDetailPathPattern.test(parsed.pathname)) return undefined;
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

/**
 * Keeps a Membership detail opened from a Customer Profile inside that exact
 * Customer Membership tab. The allowlist prevents arbitrary redirects while
 * preserving the customer tab filters and pagination that produced the record.
 */
export function normalizeCrmCustomerMembershipReturnTo(
  value: string | undefined
): string | undefined {
  if (value === undefined) return undefined;
  const parsed = splitApplicationPath(value);
  if (parsed === undefined || !customerMembershipPathPattern.test(parsed.pathname)) {
    return undefined;
  }
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

/** URL state owned by one Membership detail. */
export const membershipDetailSearchSchema = z
  .object({
    ...membershipRegisterSearchSchema.shape,
    membershipTab: z.enum(['documents']).optional(),
    edit: z
      .union([z.literal('1'), z.literal(1)])
      .transform(() => '1' as const)
      .optional(),
    returnTo: z
      .string()
      .max(4096)
      .optional()
      .transform((value) => normalizeCrmCustomerMembershipReturnTo(value)),
  })
  .strip();

export type MembershipDetailSearch = MembershipRegisterSearch & {
  membershipTab?: 'documents';
  edit?: '1';
  returnTo?: string;
};

export function normalizeMembershipDetailSearch(search: unknown): MembershipDetailSearch {
  return membershipDetailSearchSchema.parse(search);
}
